/**
 * ParceiroV2Portal — Portal Parceiros v2 do CPS CAMASA.
 *
 * Rota: /parceiro2
 * Auth: código WhatsApp 6 dígitos via Z-API, sessão Bearer 30 dias em localStorage.
 *
 * Dashboards por tipo:
 *  - motorista  → fretes ativos + GPS auto a cada 3 min
 *  - colocador  → obras + check-in/out + fotos + atualização de etapa
 *  - marmoraria → pedidos em produção + fotos + prazo + sobras
 *  - medidor    → medições + upload arquivos técnicos + relatório + cubas
 *
 * Todas as ações entram em fila partner_updates status=pending — Carlo aprova.
 * Tema Modo Campo: botões ≥72px altura, fonte 18px, contraste alto.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "cps_parc2_session";
const SESSION_PARTNER = "cps_parc2_partner";

// ============================================================================
// Helpers
// ============================================================================

const maskPhoneBR = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

/** Comprime imagem via canvas: max 1920px largura, JPEG q=0.8, alvo ≤2 MB. */
async function compressImage(file) {
  if (!file.type.startsWith("image/")) return file;  // PDF/DWG/etc passam intactos
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    img.onload = () => {
      const MAX = 1920;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const cv = document.createElement("canvas");
      cv.width = w; cv.height = h;
      cv.getContext("2d").drawImage(img, 0, 0, w, h);
      cv.toBlob((blob) => {
        if (!blob) return resolve(file);
        const out = new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
        resolve(out);
      }, "image/jpeg", 0.8);
    };
    img.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

async function uploadFile(token, file, onProgress) {
  const compressed = await compressImage(file);
  const fd = new FormData();
  fd.append("file", compressed);
  const { data } = await axios.post(`${API}/parceiro2/uploads`, fd, {
    headers: { Authorization: `Bearer ${token}` },
    onUploadProgress: e => onProgress?.(Math.round((e.loaded / (e.total || 1)) * 100)),
  });
  return data;  // { url, name, size, content_type }
}

// ============================================================================
// Tema Modo Campo — CSS inline (responsivo via JS)
// ============================================================================

const isField = () => typeof window !== "undefined" && window.innerWidth < 1024;

const themeField = {
  bg: "#0a0a0a", bg2: "#161616", bg3: "#222", ink: "#f5f5f5", ink2: "#cdc8be",
  ink3: "#8a857a", gold: "#c4a05a", line: "#2a2a2a", danger: "#c47a7a", ok: "#5fa86c",
};

const BigButton = ({ children, color = "gold", onClick, disabled, ...rest }) => {
  const colors = {
    gold: { bg: "linear-gradient(180deg,#d4b27a 0%,#c4a05a 100%)", color: "#0a0a0a", border: "transparent" },
    green: { bg: themeField.ok, color: "#fff", border: "transparent" },
    red: { bg: themeField.danger, color: "#fff", border: "transparent" },
    ghost: { bg: "transparent", color: themeField.gold, border: `1px solid ${themeField.line}` },
  }[color] || {};
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        minHeight: 72, padding: "16px 24px", fontSize: 18, fontWeight: 700,
        letterSpacing: ".05em", textTransform: "uppercase",
        border: colors.border, background: colors.bg, color: colors.color,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
        width: "100%", transition: "transform .12s",
        WebkitTapHighlightColor: "transparent",
      }}
      onTouchStart={e => { e.currentTarget.style.transform = "scale(0.96)"; }}
      onTouchEnd={e => { e.currentTarget.style.transform = "scale(1)"; }}
      {...rest}
    >
      {children}
    </button>
  );
};

const Card = ({ children, onClick, ...rest }) => (
  <div
    onClick={onClick}
    style={{
      background: themeField.bg2, border: `1px solid ${themeField.line}`,
      padding: 20, cursor: onClick ? "pointer" : "default", marginBottom: 12,
    }}
    {...rest}
  >{children}</div>
);

// ============================================================================
// Login
// ============================================================================

function LoginScreen({ onLogged }) {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const requestCode = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { setErr("Telefone inválido"); return; }
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/parceiro2/auth/request-code`, { telefone: digits });
      setStep(2);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erro ao enviar código");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) { setErr("Código deve ter 6 dígitos"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/parceiro2/auth/verify-code`, { telefone: phone.replace(/\D/g, ""), codigo: code });
      localStorage.setItem(SESSION_KEY, data.session_token);
      localStorage.setItem(SESSION_PARTNER, JSON.stringify(data.parceiro));
      onLogged(data.session_token, data.parceiro);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Código inválido");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: themeField.bg, color: themeField.ink, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 420, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 11, letterSpacing: ".35em", color: themeField.gold, marginBottom: 8 }}>CAMASA</div>
          <h1 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 36, margin: 0 }}>Portal Parceiros</h1>
        </div>
        {step === 1 ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", color: themeField.ink3 }}>Celular com DDD</label>
              <input
                value={phone}
                onChange={e => setPhone(maskPhoneBR(e.target.value))}
                placeholder="(11) 98392-6920"
                inputMode="tel"
                autoFocus
                data-testid="p2-phone-input"
                style={{ width: "100%", marginTop: 6, padding: "16px 18px", fontSize: 20, background: themeField.bg2, color: themeField.ink, border: `1px solid ${themeField.line}`, fontFamily: "monospace" }}
              />
            </div>
            <BigButton onClick={requestCode} disabled={busy} data-testid="p2-request-code">
              {busy ? "Enviando…" : "Receber código no WhatsApp"}
            </BigButton>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16, fontSize: 14, color: themeField.ink2 }}>
              Enviamos um código para <strong style={{ color: themeField.gold }}>{phone}</strong>. Verifique seu WhatsApp.
            </div>
            <input
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              data-testid="p2-code-input"
              style={{ width: "100%", marginBottom: 16, padding: "20px 18px", fontSize: 32, textAlign: "center", letterSpacing: "0.5em", background: themeField.bg2, color: themeField.gold, border: `1px solid ${themeField.gold}`, fontFamily: "monospace" }}
            />
            <BigButton onClick={verify} disabled={busy} data-testid="p2-verify-code">
              {busy ? "Verificando…" : "Entrar"}
            </BigButton>
            <button onClick={() => { setStep(1); setCode(""); }} style={{ marginTop: 12, width: "100%", background: "transparent", border: "none", color: themeField.ink3, fontSize: 13, padding: 12, cursor: "pointer" }}>← Trocar número</button>
          </>
        )}
        {err && <div style={{ marginTop: 16, padding: 12, background: "rgba(196,122,122,0.1)", border: "1px solid rgba(196,122,122,0.4)", color: themeField.danger, fontSize: 14, textAlign: "center" }}>{err}</div>}
      </div>
    </div>
  );
}

// ============================================================================
// Dashboards por tipo
// ============================================================================

function PendingBanner({ token, parc }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/parceiro2/updates/meus`, { headers: { Authorization: `Bearer ${token}` } });
        if (mounted) setCount((data || []).filter(u => u.status === "pending").length);
      } catch { /* ignora */ }
    };
    load();
    const i = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(i); };
  }, [token]);
  if (count === 0) return null;
  return (
    <div data-testid="p2-pending-banner" style={{ background: "rgba(196,160,90,0.12)", border: `1px solid ${themeField.gold}55`, color: themeField.gold, padding: 12, marginBottom: 16, fontSize: 13, letterSpacing: ".1em", textTransform: "uppercase", textAlign: "center" }}>
      ⏳ {count} envio(s) aguardando aprovação de Carlo
    </div>
  );
}

const useSendUpdate = (token) => useCallback(async (tipo_update, obra_id, dados, urgente = false) => {
  const { data } = await axios.post(`${API}/parceiro2/updates`, { tipo_update, obra_id, dados, urgente }, { headers: { Authorization: `Bearer ${token}` } });
  return data;
}, [token]);

// Captura de fotos (input file com capture=camera)
function PhotoPicker({ token, multiple = false, accept = "image/*", onPicked, label = "📷 Tirar Foto", capture = "environment" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    try {
      for (const f of files) {
        const r = await uploadFile(token, f, setProgress);
        urls.push(r);
      }
      onPicked(urls);
    } catch (err) {
      alert("Falha no upload: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false); setProgress(0);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} capture={capture} multiple={multiple} onChange={handle} style={{ display: "none" }} />
      <BigButton color="ghost" onClick={() => ref.current?.click()} disabled={busy}>
        {busy ? `📤 Enviando ${progress}%` : label}
      </BigButton>
    </>
  );
}

// ---------- MOTORISTA ----------

function MotoristaDashboard({ token, parc, obras }) {
  const send = useSendUpdate(token);
  const [active, setActive] = useState(null);

  if (active) return <MotoristaObra obra={active} token={token} onBack={() => setActive(null)} send={send} />;

  return (
    <>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 28, marginBottom: 16 }}>Fretes ativos</h2>
      {obras.length === 0 ? (
        <Card><p style={{ color: themeField.ink3, margin: 0 }}>Nenhum frete atribuído.</p></Card>
      ) : obras.map(o => (
        <Card key={o.id} onClick={() => setActive(o)} data-testid={`p2-frete-card-${o.id}`}>
          <div style={{ fontSize: 11, letterSpacing: ".2em", color: themeField.gold, textTransform: "uppercase" }}>{o.numero_orcamento ? `CPS-${o.numero_orcamento}` : o.codigo}</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, margin: "4px 0" }}>{o.nome}</div>
          <div style={{ fontSize: 13, color: themeField.ink2 }}>{o.cliente_nome} · {o.cidade}</div>
        </Card>
      ))}
    </>
  );
}

function MotoristaObra({ obra, onBack, send, token }) {
  const [tripActive, setTripActive] = useState(false);
  const [problema, setProblema] = useState(false);
  const [probText, setProbText] = useState("");
  const [probUrls, setProbUrls] = useState([]);
  const watchRef = useRef(null);

  const startTrip = async () => {
    if (!navigator.geolocation) return alert("GPS não disponível");
    setTripActive(true);
    await send("iniciar_viagem", obra.id, { iniciado_em: new Date().toISOString() });
    // ping a cada 3 min
    const ping = () => navigator.geolocation.getCurrentPosition(
      pos => axios.post(`${API}/parceiro2/gps`, {
        obra_id: obra.id, lat: pos.coords.latitude, lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy, speed: pos.coords.speed, heading: pos.coords.heading,
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {}),
      err => console.warn("gps err", err),
      { enableHighAccuracy: true, timeout: 15000 }
    );
    ping();
    watchRef.current = setInterval(ping, 3 * 60 * 1000);
  };

  const stopTrip = async () => {
    setTripActive(false);
    if (watchRef.current) { clearInterval(watchRef.current); watchRef.current = null; }
    await send("parar_viagem", obra.id, { encerrado_em: new Date().toISOString() });
  };

  useEffect(() => () => { if (watchRef.current) clearInterval(watchRef.current); }, []);

  const sendPhoto = async (tipo, urls) => {
    await send(tipo, obra.id, { fotos: urls.map(u => u.url) });
    alert("Enviado para aprovação de Carlo");
  };

  const sendProblema = async () => {
    if (!probText.trim()) return alert("Descreva o problema");
    await send("problema_frete", obra.id, { descricao: probText, fotos: probUrls.map(u => u.url) }, true);
    setProblema(false); setProbText(""); setProbUrls([]);
    alert("Problema reportado a Carlo");
  };

  return (
    <>
      <button onClick={onBack} style={{ background: "transparent", border: "none", color: themeField.gold, fontSize: 14, marginBottom: 16, cursor: "pointer" }}>← Voltar</button>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 24, marginBottom: 16 }}>{obra.nome}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <PhotoPicker token={token} onPicked={u => sendPhoto("foto_retirada", u)} label="📦 Foto Retirada (Marmoraria)" />
        {tripActive ? (
          <BigButton color="red" onClick={stopTrip} data-testid="p2-stop-trip">🛑 Parar Viagem (GPS ativo)</BigButton>
        ) : (
          <BigButton color="green" onClick={startTrip} data-testid="p2-start-trip">🚛 Iniciar Viagem (com GPS)</BigButton>
        )}
        <PhotoPicker token={token} onPicked={u => sendPhoto("foto_entrega", u)} label="🏠 Foto Entrega (Obra)" />
        {!problema ? (
          <BigButton color="red" onClick={() => setProblema(true)}>⚠ Reportar Problema</BigButton>
        ) : (
          <Card>
            <textarea value={probText} onChange={e => setProbText(e.target.value)} placeholder="Descreva o problema…" rows={4} style={{ width: "100%", padding: 12, fontSize: 16, background: themeField.bg, color: themeField.ink, border: `1px solid ${themeField.line}`, marginBottom: 12 }} />
            <PhotoPicker token={token} multiple onPicked={u => setProbUrls(prev => [...prev, ...u])} label={`📷 Anexar Foto (${probUrls.length})`} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <BigButton color="ghost" onClick={() => { setProblema(false); setProbText(""); setProbUrls([]); }}>Cancelar</BigButton>
              <BigButton color="red" onClick={sendProblema}>Enviar URGENTE</BigButton>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

// ---------- COLOCADOR ----------

function ColocadorDashboard({ token, parc, obras }) {
  const send = useSendUpdate(token);
  const [active, setActive] = useState(null);
  if (active) return <ColocadorObra obra={active} token={token} send={send} onBack={() => setActive(null)} />;
  return (
    <>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 28, marginBottom: 16 }}>Obras atribuídas</h2>
      {obras.length === 0 ? (
        <Card><p style={{ color: themeField.ink3, margin: 0 }}>Nenhuma obra atribuída.</p></Card>
      ) : obras.map(o => (
        <Card key={o.id} onClick={() => setActive(o)} data-testid={`p2-obra-card-${o.id}`}>
          <div style={{ fontSize: 11, letterSpacing: ".2em", color: themeField.gold, textTransform: "uppercase" }}>{o.numero_orcamento ? `CPS-${o.numero_orcamento}` : o.codigo}</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, margin: "4px 0" }}>{o.nome}</div>
          <div style={{ fontSize: 13, color: themeField.ink2 }}>{o.cliente_nome} · {o.cidade}</div>
        </Card>
      ))}
    </>
  );
}

const PROBLEMA_MAT_TIPOS = ["peça errada", "danificada", "fora de medida", "cor errada", "outro"];

function ColocadorObra({ obra, send, token, onBack }) {
  const [view, setView] = useState("home");
  const [legenda, setLegenda] = useState("");
  const [urls, setUrls] = useState([]);
  const [etapaSel, setEtapaSel] = useState("");
  const [statusSel, setStatusSel] = useState("em_andamento");
  const [obs, setObs] = useState("");
  const [probTipo, setProbTipo] = useState(PROBLEMA_MAT_TIPOS[0]);

  const finish = async (tipo, dados, urgente = false) => {
    await send(tipo, obra.id, dados, urgente);
    setView("home"); setLegenda(""); setUrls([]); setObs("");
    alert("Enviado para aprovação de Carlo");
  };

  if (view === "fotos") return (
    <FormScreen onBack={() => setView("home")} title="Fotos do dia">
      <PhotoPicker token={token} multiple onPicked={u => setUrls(prev => [...prev, ...u])} label={`📷 Adicionar Fotos (${urls.length})`} />
      <textarea value={legenda} onChange={e => setLegenda(e.target.value)} placeholder="Legenda (opcional)" rows={3} style={txStyle} />
      <BigButton color="green" onClick={() => finish("fotos_diarias", { fotos: urls.map(u => u.url), legenda })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "atualiza") return (
    <FormScreen onBack={() => setView("home")} title="Atualizar Etapa">
      <select value={etapaSel} onChange={e => setEtapaSel(e.target.value)} style={selStyle}>
        <option value="">— Etapa —</option>
        {(obra.etapas || []).map((e, i) => <option key={i} value={e.nome}>{e.nome}</option>)}
      </select>
      <select value={statusSel} onChange={e => setStatusSel(e.target.value)} style={selStyle}>
        <option value="em_andamento">Em Andamento</option>
        <option value="concluida">Concluída</option>
        <option value="aguardando">Aguardando</option>
      </select>
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Observação" rows={3} style={txStyle} />
      <BigButton color="green" disabled={!etapaSel} onClick={() => finish("atualizacao_etapa", { etapa: etapaSel, status: statusSel, observacao: obs })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "concluir") return (
    <FormScreen onBack={() => setView("home")} title="Concluir Etapa">
      <select value={etapaSel} onChange={e => setEtapaSel(e.target.value)} style={selStyle}>
        <option value="">— Etapa —</option>
        {(obra.etapas || []).map((e, i) => <option key={i} value={e.nome}>{e.nome}</option>)}
      </select>
      <input type="date" defaultValue={new Date().toISOString().slice(0, 10)} id="conc-data" style={selStyle} />
      <PhotoPicker token={token} onPicked={u => setUrls(u)} label={`📷 Foto Obrigatória (${urls.length}/1)`} />
      <BigButton color="green" disabled={!etapaSel || urls.length === 0} onClick={() => finish("conclusao_etapa", { etapa: etapaSel, data: document.getElementById("conc-data").value, foto: urls[0]?.url })}>Confirmar Conclusão</BigButton>
    </FormScreen>
  );

  if (view === "probmat") return (
    <FormScreen onBack={() => setView("home")} title="Problema com Material">
      <select value={probTipo} onChange={e => setProbTipo(e.target.value)} style={selStyle}>
        {PROBLEMA_MAT_TIPOS.map(t => <option key={t}>{t}</option>)}
      </select>
      <PhotoPicker token={token} onPicked={u => setUrls(u)} label={`📷 Foto (${urls.length})`} />
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Observação" rows={3} style={txStyle} />
      <BigButton color="red" onClick={() => finish("problema_material", { tipo: probTipo, foto: urls[0]?.url, observacao: obs }, true)}>Enviar URGENTE</BigButton>
    </FormScreen>
  );

  if (view === "probext") return (
    <FormScreen onBack={() => setView("home")} title="Problema Externo">
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Ex: falta cimento-cola, acesso bloqueado…" rows={5} style={txStyle} />
      <PhotoPicker token={token} onPicked={u => setUrls(u)} label={`📷 Foto Opcional (${urls.length})`} />
      <BigButton color="red" disabled={!obs} onClick={() => finish("problema_externo", { descricao: obs, foto: urls[0]?.url }, true)}>Enviar URGENTE</BigButton>
    </FormScreen>
  );

  return (
    <>
      <button onClick={onBack} style={{ background: "transparent", border: "none", color: themeField.gold, fontSize: 14, marginBottom: 16, cursor: "pointer" }}>← Voltar</button>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 24, marginBottom: 16 }}>{obra.nome}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <BigButton color="green" onClick={() => send("checkin", obra.id, { hora: new Date().toISOString() }).then(() => alert("Check-in enviado"))}>☀ Iniciar Dia</BigButton>
        <BigButton color="ghost" onClick={() => send("checkout", obra.id, { hora: new Date().toISOString() }).then(() => alert("Check-out enviado"))}>🌙 Encerrar Dia</BigButton>
        <BigButton color="gold" onClick={() => setView("fotos")}>📷 Fotos do Dia</BigButton>
        <BigButton color="gold" onClick={() => setView("atualiza")}>✏ Atualizar Etapa</BigButton>
        <BigButton color="gold" onClick={() => setView("concluir")}>✓ Concluir Etapa</BigButton>
        <BigButton color="red" onClick={() => setView("probmat")}>⚠ Problema Material</BigButton>
        <BigButton color="red" onClick={() => setView("probext")}>⚠ Problema Externo</BigButton>
      </div>
    </>
  );
}

// ---------- MARMORARIA ----------

const PROBLEMA_PROD_TIPOS = ["quebra", "mancha", "veio errado", "espessura errada", "outro"];

function MarmorariaDashboard({ token, parc, obras }) {
  const send = useSendUpdate(token);
  const [active, setActive] = useState(null);
  if (active) return <MarmorariaObra obra={active} token={token} send={send} onBack={() => setActive(null)} />;
  return (
    <>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 28, marginBottom: 16 }}>Pedidos em produção</h2>
      {obras.length === 0 ? (
        <Card><p style={{ color: themeField.ink3, margin: 0 }}>Nenhum pedido atribuído.</p></Card>
      ) : obras.map(o => (
        <Card key={o.id} onClick={() => setActive(o)} data-testid={`p2-pedido-card-${o.id}`}>
          <div style={{ fontSize: 11, letterSpacing: ".2em", color: themeField.gold, textTransform: "uppercase" }}>{o.numero_orcamento ? `CPS-${o.numero_orcamento}` : o.codigo}</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, margin: "4px 0" }}>{o.nome}</div>
          <div style={{ fontSize: 13, color: themeField.ink2 }}>{o.cliente_nome}</div>
        </Card>
      ))}
    </>
  );
}

function MarmorariaObra({ obra, send, token, onBack }) {
  const [view, setView] = useState("home");
  const [urls, setUrls] = useState([]);
  const [obs, setObs] = useState("");
  const [prazo, setPrazo] = useState("");
  const [probTipo, setProbTipo] = useState(PROBLEMA_PROD_TIPOS[0]);

  const finish = async (tipo, dados, urgente = false) => {
    await send(tipo, obra.id, dados, urgente);
    setView("home"); setUrls([]); setObs(""); setPrazo("");
    alert("Enviado para aprovação de Carlo");
  };

  if (view === "fotos") return (
    <FormScreen onBack={() => setView("home")} title="Fotos Produção">
      <PhotoPicker token={token} multiple onPicked={u => setUrls(prev => [...prev, ...u])} label={`📷 Fotos (${urls.length})`} />
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Legenda" rows={3} style={txStyle} />
      <BigButton color="green" disabled={urls.length === 0} onClick={() => finish("fotos_producao", { fotos: urls.map(u => u.url), legenda: obs })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "prazo") return (
    <FormScreen onBack={() => setView("home")} title="Atualizar Prazo">
      <input type="date" value={prazo} onChange={e => setPrazo(e.target.value)} style={selStyle} />
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Justificativa" rows={4} style={txStyle} />
      <BigButton color="green" disabled={!prazo || !obs} onClick={() => finish("atualizacao_prazo", { nova_data: prazo, justificativa: obs })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "pronto") return (
    <FormScreen onBack={() => setView("home")} title="Pronto Para Frete">
      <input type="date" value={prazo} onChange={e => setPrazo(e.target.value)} style={selStyle} />
      <PhotoPicker token={token} multiple onPicked={u => setUrls(prev => [...prev, ...u])} label={`📷 Fotos (mín 2) — ${urls.length}`} />
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Observações" rows={3} style={txStyle} />
      <BigButton color="green" disabled={urls.length < 2 || !prazo} onClick={() => finish("pronto_frete", { data: prazo, fotos: urls.map(u => u.url), observacoes: obs })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "probprod") return (
    <FormScreen onBack={() => setView("home")} title="Problema Produção">
      <select value={probTipo} onChange={e => setProbTipo(e.target.value)} style={selStyle}>
        {PROBLEMA_PROD_TIPOS.map(t => <option key={t}>{t}</option>)}
      </select>
      <PhotoPicker token={token} onPicked={u => setUrls(u)} label={`📷 Foto (${urls.length})`} />
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Impacto no prazo?" rows={3} style={txStyle} />
      <BigButton color="red" onClick={() => finish("problema_producao", { tipo: probTipo, foto: urls[0]?.url, impacto_prazo: obs }, true)}>Enviar URGENTE</BigButton>
    </FormScreen>
  );

  if (view === "sobras") return (
    <FormScreen onBack={() => setView("home")} title="Sobras de Material">
      <input id="sob-qtd" placeholder="Quantidade (ex: 1.5 m²)" style={selStyle} />
      <textarea id="sob-desc" placeholder="Descrição do material" rows={3} style={txStyle} />
      <input id="sob-local" placeholder="Onde ficou armazenado" style={selStyle} />
      <BigButton color="green" onClick={() => finish("sobras_material", {
        quantidade: document.getElementById("sob-qtd").value,
        descricao: document.getElementById("sob-desc").value,
        local: document.getElementById("sob-local").value,
      })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "atraso") return (
    <FormScreen onBack={() => setView("home")} title="Atraso de Produção">
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Motivo do atraso" rows={3} style={txStyle} />
      <input type="date" value={prazo} onChange={e => setPrazo(e.target.value)} style={selStyle} />
      <BigButton color="red" disabled={!obs || !prazo} onClick={() => finish("atraso_producao", { motivo: obs, nova_data: prazo }, true)}>Enviar</BigButton>
    </FormScreen>
  );

  return (
    <>
      <button onClick={onBack} style={{ background: "transparent", border: "none", color: themeField.gold, fontSize: 14, marginBottom: 16, cursor: "pointer" }}>← Voltar</button>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 24, marginBottom: 16 }}>{obra.nome}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <BigButton color="gold" onClick={() => setView("fotos")}>📷 Fotos Produção</BigButton>
        <BigButton color="gold" onClick={() => setView("prazo")}>📅 Atualizar Prazo</BigButton>
        <BigButton color="green" onClick={() => setView("pronto")}>✓ Pronto Para Frete</BigButton>
        <BigButton color="red" onClick={() => setView("probprod")}>⚠ Problema Produção</BigButton>
        <BigButton color="ghost" onClick={() => setView("sobras")}>📦 Registrar Sobras</BigButton>
        <BigButton color="red" onClick={() => setView("atraso")}>⏰ Registrar Atraso</BigButton>
      </div>
    </>
  );
}

// ---------- MEDIDOR ----------

const ESTADO_CUBA = ["ótimo", "bom", "danificado"];

function MedidorDashboard({ token, parc, obras }) {
  const send = useSendUpdate(token);
  const [active, setActive] = useState(null);
  if (active) return <MedidorObra obra={active} token={token} send={send} onBack={() => setActive(null)} />;
  return (
    <>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 28, marginBottom: 16 }}>Medições</h2>
      {obras.length === 0 ? (
        <Card><p style={{ color: themeField.ink3, margin: 0 }}>Nenhuma medição atribuída.</p></Card>
      ) : obras.map(o => (
        <Card key={o.id} onClick={() => setActive(o)} data-testid={`p2-med-card-${o.id}`}>
          <div style={{ fontSize: 11, letterSpacing: ".2em", color: themeField.gold, textTransform: "uppercase" }}>{o.numero_orcamento ? `CPS-${o.numero_orcamento}` : o.codigo}</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, margin: "4px 0" }}>{o.nome}</div>
          <div style={{ fontSize: 13, color: themeField.ink2 }}>{o.cidade}</div>
        </Card>
      ))}
    </>
  );
}

function MedidorObra({ obra, send, token, onBack }) {
  const [view, setView] = useState("home");
  const [urls, setUrls] = useState([]);
  const [obs, setObs] = useState("");
  const [estado, setEstado] = useState(ESTADO_CUBA[0]);

  const finish = async (tipo, dados) => {
    await send(tipo, obra.id, dados);
    setView("home"); setUrls([]); setObs("");
    alert("Enviado para aprovação de Carlo");
  };

  if (view === "upload") return (
    <FormScreen onBack={() => setView("home")} title="Upload Arquivos Técnicos">
      <PhotoPicker token={token} multiple capture={null} accept=".pdf,.dwg,.dxf,.xls,.xlsx,.jpg,.jpeg,.png,application/pdf" onPicked={u => setUrls(prev => [...prev, ...u])} label={`📎 Adicionar Arquivos (${urls.length})`} />
      {urls.map((f, i) => (
        <div key={i} style={{ padding: 10, background: themeField.bg, border: `1px solid ${themeField.line}`, fontSize: 13, color: themeField.ink2 }}>📄 {f.name} <span style={{ color: themeField.ink3 }}>({Math.round(f.size / 1024)} KB)</span></div>
      ))}
      <BigButton color="green" disabled={urls.length === 0} onClick={() => finish("upload_medicao", { arquivos: urls.map(u => ({ url: u.url, nome: u.name, content_type: u.content_type })) })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "fotos") return (
    <FormScreen onBack={() => setView("home")} title="Fotos do Local">
      <PhotoPicker token={token} multiple onPicked={u => setUrls(prev => [...prev, ...u])} label={`📷 Fotos (${urls.length})`} />
      <BigButton color="green" disabled={urls.length === 0} onClick={() => finish("fotos_medicao", { fotos: urls.map(u => u.url) })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "relatorio") return (
    <FormScreen onBack={() => setView("home")} title="Relatório de Medição">
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Relatório técnico…" rows={10} style={txStyle} />
      <BigButton color="green" disabled={!obs} onClick={() => finish("relatorio_medicao", { texto: obs })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "coment") return (
    <FormScreen onBack={() => setView("home")} title="Comentários Técnicos">
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Observações técnicas…" rows={6} style={txStyle} />
      <BigButton color="green" disabled={!obs} onClick={() => finish("comentarios_medicao", { texto: obs })}>Enviar</BigButton>
    </FormScreen>
  );

  if (view === "cubas") return (
    <FormScreen onBack={() => setView("home")} title="Retirada de Cubas">
      <textarea id="cuba-quais" placeholder="Quais cubas (ex: cuba inox 50x40 cozinha)" rows={2} style={txStyle} />
      <input id="cuba-qtd" type="number" placeholder="Quantidade" style={selStyle} />
      <select value={estado} onChange={e => setEstado(e.target.value)} style={selStyle}>
        {ESTADO_CUBA.map(s => <option key={s}>{s}</option>)}
      </select>
      <PhotoPicker token={token} onPicked={u => setUrls(u)} label={`📷 Foto (${urls.length})`} />
      <input id="cuba-dest" placeholder="Destino" style={selStyle} />
      <BigButton color="green" onClick={() => finish("retirada_cubas", {
        quais: document.getElementById("cuba-quais").value,
        qtd: parseInt(document.getElementById("cuba-qtd").value || "0", 10),
        estado,
        foto: urls[0]?.url,
        destino: document.getElementById("cuba-dest").value,
      })}>Enviar</BigButton>
    </FormScreen>
  );

  return (
    <>
      <button onClick={onBack} style={{ background: "transparent", border: "none", color: themeField.gold, fontSize: 14, marginBottom: 16, cursor: "pointer" }}>← Voltar</button>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 24, marginBottom: 16 }}>{obra.nome}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <BigButton color="green" onClick={() => send("checkin_medicao", obra.id, { hora: new Date().toISOString() }).then(() => alert("Visita iniciada"))}>🏠 Iniciar Visita</BigButton>
        <BigButton color="gold" onClick={() => setView("upload")}>📎 Upload Arquivos</BigButton>
        <BigButton color="gold" onClick={() => setView("fotos")}>📷 Fotos do Local</BigButton>
        <BigButton color="gold" onClick={() => setView("relatorio")}>📝 Relatório</BigButton>
        <BigButton color="gold" onClick={() => setView("coment")}>💬 Comentários Técnicos</BigButton>
        <BigButton color="ghost" onClick={() => setView("cubas")}>🛁 Retirada de Cubas</BigButton>
        <BigButton color="green" onClick={() => send("conclusao_medicao", obra.id, {}).then(() => alert("Medição concluída"))}>✓ Concluir Medição</BigButton>
      </div>
    </>
  );
}

// ============================================================================
// Estilos compartilhados + FormScreen helper
// ============================================================================

const selStyle = {
  width: "100%", padding: "16px 18px", fontSize: 18, background: themeField.bg2,
  color: themeField.ink, border: `1px solid ${themeField.line}`, marginBottom: 12,
};

const txStyle = {
  width: "100%", padding: 12, fontSize: 16, background: themeField.bg2,
  color: themeField.ink, border: `1px solid ${themeField.line}`, marginBottom: 12,
  fontFamily: "inherit", resize: "vertical",
};

function FormScreen({ onBack, title, children }) {
  return (
    <>
      <button onClick={onBack} style={{ background: "transparent", border: "none", color: themeField.gold, fontSize: 14, marginBottom: 16, cursor: "pointer" }}>← Voltar</button>
      <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 24, marginBottom: 16 }}>{title}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </>
  );
}

// ============================================================================
// Shell principal
// ============================================================================

export default function ParceiroV2Portal() {
  const [token, setToken] = useState(() => localStorage.getItem(SESSION_KEY));
  const [parc, setParc] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SESSION_PARTNER) || "null"); } catch { return null; }
  });
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/parceiro2/me`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/parceiro2/obras`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(([me, ob]) => {
      setParc(me.data);
      localStorage.setItem(SESSION_PARTNER, JSON.stringify(me.data));
      setObras(ob.data || []);
    }).catch((e) => {
      if (e?.response?.status === 401) {
        localStorage.removeItem(SESSION_KEY); localStorage.removeItem(SESSION_PARTNER);
        setToken(null); setParc(null);
      }
    }).finally(() => setLoading(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(SESSION_KEY); localStorage.removeItem(SESSION_PARTNER);
    setToken(null); setParc(null);
  };

  if (!token || !parc) return <LoginScreen onLogged={(t, p) => { setToken(t); setParc(p); }} />;

  const Dashboard = {
    motorista: MotoristaDashboard, colocador: ColocadorDashboard,
    marmoraria: MarmorariaDashboard, medidor: MedidorDashboard,
  }[parc.tipo] || (() => <Card><p style={{ color: themeField.ink3 }}>Tipo &quot;{parc.tipo}&quot; ainda não implementado.</p></Card>);

  return (
    <div style={{ minHeight: "100vh", background: themeField.bg, color: themeField.ink }}>
      <header style={{ padding: "16px 20px", borderBottom: `1px solid ${themeField.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: ".3em", color: themeField.gold }}>CAMASA · PARCEIROS</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18 }}>{parc.nome}</div>
          <div style={{ fontSize: 11, color: themeField.ink3, textTransform: "uppercase", letterSpacing: ".15em" }}>{parc.tipo}</div>
        </div>
        <button onClick={logout} data-testid="p2-logout" style={{ background: "transparent", border: `1px solid ${themeField.line}`, color: themeField.ink3, padding: "8px 12px", fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", cursor: "pointer" }}>Sair</button>
      </header>
      <main style={{ maxWidth: 720, margin: "0 auto", padding: 20 }}>
        <PendingBanner token={token} parc={parc} />
        {loading ? <p style={{ color: themeField.ink3 }}>Carregando…</p> : <Dashboard token={token} parc={parc} obras={obras} />}
      </main>
    </div>
  );
}
